import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import {
  useCreateReadinessScoreMutation,
  useOverrideReadinessScoreMutation,
  type ListScoreItem,
} from '@/lib/api/readiness-scorecard';
import { useOverrideTaxonomyQuery } from '@/lib/api/overrides';
import type { RubricCriterion } from '@/lib/api/readiness-rubric';
import { titleCase } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * Score a Go-Live readiness criterion (verification pass — previously NO
 * UI path could create or change a score; POST /readiness/scores and
 * PATCH /readiness/scores/:id both had zero callers, so the scorecard
 * could never fill in).
 *
 * Unscored criterion → create (origin manually_authored, typed input per
 * score_type). Scored criterion → value change via PATCH, which the API
 * requires to carry a structured override {tag, rationale}.
 *
 * T-0238: opened as "Score differently" against a pending AI proposal
 * (`aiProposalId`), the score answers it: the proposal is marked overridden
 * (or accepted, when the value chosen is the AI's) and leaves the review
 * queue, so the AI's reading stays in the history beside the score.
 */
export function ScoreCriterionModal({
  engagementId,
  criterion,
  score,
  aiProposalId = null,
  open,
  onOpenChange,
}: {
  engagementId: string;
  criterion: RubricCriterion;
  score: ListScoreItem | null;
  /** The pending AI proposal this score answers, when scoring differently. */
  aiProposalId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = score?.score_id != null;
  const [value, setValue] = useState<string>(score?.score != null ? String(score.score) : '');
  const [recommendation, setRecommendation] = useState('');
  const [tag, setTag] = useState('');
  const [rationale, setRationale] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateReadinessScoreMutation(engagementId);
  const override = useOverrideReadinessScoreMutation(engagementId);
  const taxonomy = useOverrideTaxonomyQuery();

  const aiCandidate = score?.ai_candidate ?? null;
  const scoreType = criterion.score_type;
  const numeric = scoreType === 'numeric_0_10';
  const options =
    scoreType === 'pass_fail'
      ? ['pass', 'fail']
      : scoreType === 'ryg'
        ? ['red', 'yellow', 'green']
        : [];

  const valueValid = numeric
    ? value !== '' && Number(value) >= 0 && Number(value) <= 10
    : value !== '';
  const overrideValid = !isEdit || (tag.length > 0 && rationale.trim().length >= 10);
  const pending = create.isPending || override.isPending;

  function submit() {
    setError(null);
    const parsed = numeric ? Number(value) : value;
    const onError = (e: unknown) =>
      setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Save failed.');
    if (isEdit && score?.score_id) {
      override.mutate(
        {
          scoreId: score.score_id,
          input: {
            score: parsed,
            ...(recommendation.trim() ? { recommendation: recommendation.trim() } : {}),
            override: { tag, rationale: rationale.trim() },
            ...(aiProposalId ? { ai_proposal_id: aiProposalId, kept_links: [] } : {}),
          },
          idempotencyKey: crypto.randomUUID(),
        },
        { onSuccess: () => onOpenChange(false), onError },
      );
    } else {
      create.mutate(
        {
          body: {
            criterion_id: criterion.id,
            score: parsed,
            ...(aiProposalId
              ? {
                  origin: 'ai_extracted_edited_by_consultant' as const,
                  ai_proposal_id: aiProposalId,
                  kept_links: [],
                }
              : { origin: 'manually_authored' as const }),
            ...(recommendation.trim() ? { recommendation: recommendation.trim() } : {}),
          },
          idempotencyKey: crypto.randomUUID(),
        },
        { onSuccess: () => onOpenChange(false), onError },
      );
    }
  }

  const inputCls =
    'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(480px,94vw)] -translate-x-1/2 -translate-y-1/2',
            'rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-h2 font-semibold text-ink-900">
                {isEdit ? 'Change score' : 'Score criterion'}
              </Dialog.Title>
              <p className="mt-1 truncate text-meta text-ink-500">
                {criterion.name} · {titleCase(criterion.category)}
              </p>
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>

          <div className="space-y-4 px-5 py-4">
            {aiCandidate ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ai-surface-border bg-ai-surface px-3 py-2">
                <span className="text-meta text-ai-ink">
                  AI proposed <strong className="font-mono">{String(aiCandidate.score)}</strong>
                  {' · '}
                  {Math.round(aiCandidate.confidence * 100)}% confidence
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2"
                  disabled={String(value) === String(aiCandidate.score)}
                  onClick={() => setValue(String(aiCandidate.score))}
                >
                  Use AI value
                </Button>
              </div>
            ) : null}
            {numeric ? (
              <label className="block text-meta font-medium text-ink-700">
                Score (0–10)
                <input
                  type="number"
                  min={0}
                  max={10}
                  step={0.5}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className={inputCls}
                />
              </label>
            ) : (
              <div>
                <p className="text-meta font-medium text-ink-700">Score</p>
                <div className="mt-1 flex gap-2">
                  {options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setValue(opt)}
                      className={cn(
                        'rounded-card border px-3 py-2 text-body-sm font-medium',
                        value === opt
                          ? 'border-accent-amber-600 bg-accent-amber-50 text-ink-900'
                          : 'border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-50',
                      )}
                    >
                      {titleCase(opt)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="block text-meta font-medium text-ink-700">
              Recommendation (optional)
              <input
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
                className={inputCls}
              />
            </label>

            {isEdit ? (
              <>
                <hr className="border-ink-100" />
                <p className="text-meta text-ink-700">
                  Changing a committed score requires a structured override — the prior value stays
                  in the score history. That includes reverting to the AI value: the revert is
                  itself a recorded decision.
                </p>
                <label className="block text-meta font-medium text-ink-700">
                  Override reason
                  <select value={tag} onChange={(e) => setTag(e.target.value)} className={inputCls}>
                    <option value="">— pick a reason —</option>
                    {(taxonomy.data?.tags ?? []).map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-meta font-medium text-ink-700">
                  Rationale (min 10 characters)
                  <textarea
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                    rows={3}
                    className={inputCls}
                  />
                </label>
              </>
            ) : null}

            {error ? (
              <p className="rounded-card border border-cite-broken bg-paper-0 p-3 text-meta font-medium text-cite-broken">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              size="sm"
              disabled={!valueValid || !overrideValid || pending}
              onClick={submit}
            >
              {pending ? <Spinner /> : null}
              {isEdit ? 'Apply change' : 'Commit score'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
